import type { SegmentDefinition } from '@masters-club/validation';

export type TriggerEventType =
  | "SUBSCRIPTION_PURCHASED"
  | "SUBSCRIPTION_STARTED"
  | "SUBSCRIPTION_EXPIRING"
  | "SUBSCRIPTION_EXPIRED"
  | "SUBSCRIPTION_EXPIRATION"
  | "ORDER_CREATED"
  | "ORDER_PAID"
  | "ORDER_CANCELLED"
  | "COURSE_COMPLETED"
  | "HOMEWORK_SUBMITTED"
  | "USER_REGISTERED"
  | "NEW_COURSE_AVAILABLE"
  | "CHANNEL_PASS_LIMIT_REACHED"
  | "CHANNEL_PASS_EXPIRED"
  | "COURSE_ACCESS_EXPIRED"
  | "COURSE_DRIP_RELEASED"
  | "FAMILY_MEMBER_ADDED"
  | "FAMILY_MEMBER_REMOVED"
  | "FAMILY_SLOTS_CHANGED"
  | "ACCESS_GRANTED";

export type TriggerActionType =
  | "send_notification"
  | "send_email"
  | "grant_access"
  | "revoke_access"
  | "send_broadcast"
  | "update_status"
  | "webhook"
  | "deactivate_channel_pass"
  | "hide_channel_pass"
  | "change_family_slots"
  | "send_channel_invite";

export const TRIGGER_EVENT_OPTIONS: { value: TriggerEventType; label: string }[] = [
  { value: "SUBSCRIPTION_PURCHASED", label: "Покупка подписки" },
  { value: "SUBSCRIPTION_STARTED", label: "Активация подписки" },
  { value: "SUBSCRIPTION_EXPIRING", label: "Подписка скоро истечёт" },
  { value: "SUBSCRIPTION_EXPIRED", label: "Подписка истекла" },
  { value: "SUBSCRIPTION_EXPIRATION", label: "Дата истечения подписки" },
  { value: "ORDER_CREATED", label: "Создан заказ" },
  { value: "ORDER_PAID", label: "Заказ оплачен" },
  { value: "ORDER_CANCELLED", label: "Заказ отменён" },
  { value: "COURSE_COMPLETED", label: "Курс завершён" },
  { value: "HOMEWORK_SUBMITTED", label: "Домашнее задание отправлено" },
  { value: "USER_REGISTERED", label: "Регистрация пользователя" },
  { value: "NEW_COURSE_AVAILABLE", label: "Новый курс доступен" },
  { value: "CHANNEL_PASS_LIMIT_REACHED", label: "Исчерпан лимит доступа к каналу" },
  { value: "CHANNEL_PASS_EXPIRED", label: "Доступ к каналу истёк" },
  { value: "COURSE_ACCESS_EXPIRED", label: "Доступ к курсу истёк" },
  { value: "COURSE_DRIP_RELEASED", label: "Материал курса разблокирован" },
  { value: "FAMILY_MEMBER_ADDED", label: "Добавлен член семьи" },
  { value: "FAMILY_MEMBER_REMOVED", label: "Удалён член семьи" },
  { value: "FAMILY_SLOTS_CHANGED", label: "Изменено количество слотов семьи" },
  { value: "ACCESS_GRANTED", label: "Выдан доступ (low-level)" },
];

export const TRIGGER_ACTION_OPTIONS: { value: TriggerActionType; label: string }[] = [
  { value: "send_notification", label: "Отправить уведомление" },
  { value: "send_email", label: "Отправить email" },
  { value: "grant_access", label: "Выдать доступ" },
  { value: "revoke_access", label: "Отозвать доступ" },
  { value: "send_broadcast", label: "Запустить рассылку" },
  { value: "update_status", label: "Обновить статус" },
  { value: "webhook", label: "Вызвать webhook" },
  { value: "deactivate_channel_pass", label: "Деактивировать пропуск канала" },
  { value: "hide_channel_pass", label: "Скрыть пропуск из магазина" },
  { value: "change_family_slots", label: "Изменить слоты семейной подписки" },
  { value: "send_channel_invite", label: "Отправить приглашение в канал" },
];

/** Inline-кнопка Telegram в send_notification (url / web_app). */
export interface TriggerTelegramButton {
  /** Текст (ru / дефолт). */
  text: string;
  url?: string;
  /** HTTPS URL платформы — Telegram откроет Mini App. */
  webAppUrl?: string;
  callbackData?: string;
  /** Переводы текста: en / uz / … */
  translations?: Record<string, { text?: string }>;
}

export interface TriggerAction {
  type: TriggerActionType;
  // send_notification
  channel?: "APP" | "EMAIL" | "TELEGRAM";
  templateId?: string;
  title?: string;
  message?: string;
  url?: string;
  /** Кнопки под Telegram-сообщением (шаблоны {{platformUrl}} поддерживаются). */
  buttons?: TriggerTelegramButton[];
  // send_email
  subject?: string;
  body?: string;
  // grant_access / revoke_access
  targetType?: "COURSE" | "MODULE" | "MODULE_ITEM" | "CHANNEL" | "EVENT" | "CONTENT";
  targetId?: string;
  durationDays?: number;
  // send_broadcast
  broadcastId?: string;
  // update_status
  status?: string;
  // webhook
  method?: "POST" | "GET";
  payload?: string;
  headers?: Record<string, string>;
  // deactivate_channel_pass / hide_channel_pass
  channelPassId?: number;
  // change_family_slots
  subscriptionId?: number;
  delta?: number;
  // send_channel_invite
  channelId?: number;
  // Переводы inline текстов (send_notification: title/message, send_email: subject/body)
  translations?: Record<string, Record<string, string>>;
}

export interface Trigger {
  id: string;
  name: string;
  eventType: TriggerEventType;
  actions: TriggerAction[];
  delay: number | null;
  isActive: boolean;
  audienceDefinition: SegmentDefinition | null;
  audienceSegmentId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface TriggerFormData {
  name: string;
  eventType: TriggerEventType;
  actions: TriggerAction[];
  delay: number;
  isActive: boolean;
  audienceDefinition: SegmentDefinition | null;
  audienceSegmentId: number | null;
}

/**
 * События, рассылающиеся на всю аудиторию (broadcast). Для них audience обязательна —
 * без неё триггер никому ничего не отправит.
 */
export const BROADCAST_EVENT_TYPES: ReadonlySet<TriggerEventType> = new Set([
  'NEW_COURSE_AVAILABLE',
]);

export function isBroadcastEvent(eventType: TriggerEventType | null): boolean {
  return eventType !== null && BROADCAST_EVENT_TYPES.has(eventType);
}
